import { useEffect, useState } from 'react';
import BottomPanel from './BottomPanel';
import { getUserSettings, submitFeedback } from '../services/api';

/**
 * Feedback — bottom panel for submitting feedback to the JulyNote team.
 *
 * Body holds the required feedback textarea; the footer holds the optional
 * contact email (auto-filled from the account) + the send button. On success
 * the body swaps to a thank-you state.
 *
 * Props:
 *  - show     : boolean
 *  - onClose  : () => void
 */
export default function FeedbackPanel({ show, onClose }) {
  const [content, setContent] = useState('');
  const [email, setEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  // Reset the form and auto-fill the account email when the panel opens.
  useEffect(() => {
    if (!show) return;
    setContent('');
    setEmail('');
    setError('');
    setSent(false);
    let cancelled = false;
    getUserSettings()
      .then((data) => { if (!cancelled && data?.email) setEmail(data.email); })
      .catch(() => { /* ignore — leave email empty */ });
    return () => { cancelled = true; };
  }, [show]);

  const handleSubmit = async () => {
    if (!content.trim() || sending) return;
    setSending(true);
    setError('');
    try {
      await submitFeedback(content.trim(), email.trim());
      setSent(true);
    } catch (e) {
      setError(e.message || 'Failed to send feedback');
    } finally {
      setSending(false);
    }
  };

  return (
    <BottomPanel
      show={show}
      title="Feedback"
      onClose={onClose}
      footer={
        !sent && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
            <div>
              <label className="ds-label">Contact email (optional)</label>
              <input
                type="email"
                className="ds-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                maxLength={254}
                style={{ marginTop: 'var(--space-xs)' }}
              />
              <p className="ds-hint">
                Add your email if you'd like us to reply to your feedback.
              </p>
            </div>

            {error && (
              <div style={{ fontSize: 'var(--text-sm)', color: 'var(--danger)' }}>
                {error}
              </div>
            )}

            <button
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={sending || !content.trim()}
              style={{ width: '100%' }}
            >
              {sending ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        )
      }
    >
      {sent ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', padding: 'var(--space-md)' }}>
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--ink)', lineHeight: 'var(--leading)' }}>
            Thanks for your feedback!
          </p>
          <button className="btn btn-primary" onClick={onClose} style={{ width: '100%' }}>
            Close
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)', padding: 'var(--space-md)' }}>
          <div>
            <label className="ds-label">Your feedback</label>
            <textarea
              className="ds-input"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Tell us what you think…"
              maxLength={5000}
              rows={5}
              style={{
                marginTop: 'var(--space-xs)',
                minHeight: 120,
                resize: 'none',
                fontFamily: 'var(--font-body)',
                lineHeight: 'var(--leading)',
              }}
            />
          </div>
        </div>
      )}
    </BottomPanel>
  );
}
